import React, { ComponentProps, ReactNode } from 'react';

import PropTypes from 'prop-types';

import { BasicButton } from '@adsk/alloy-react-button';
import { variantSwitch } from '@adsk/alloy-react-helpers';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import { DATE_PICKER_CELL_VARIANTS } from '../consts';
import { CellVariant } from '../types';

type CellProps = ComponentProps<typeof BasicButton> & {
  variant?: CellVariant;
  selected?: boolean;
  today?: boolean;
  hasEvent?: boolean;
  tooltipContent?: ReactNode;
};

const Cell = React.forwardRef<HTMLButtonElement, CellProps>(
  (
    {
      className,
      style,
      variant = DATE_PICKER_CELL_VARIANTS.DEFAULT,
      selected,
      today,
      disabled,
      hasEvent,
      tooltipContent,
      ...props
    },
    ref,
  ) => {
    const children = (
      <BasicButton
        {...props}
        ref={ref}
        className={className}
        tabIndex={variant === DATE_PICKER_CELL_VARIANTS.SPACER ? -1 : undefined}
        style={[
          {
            width: 40,
            height: 40,
            cursor: disabled ? 'default' : 'pointer',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'inline-flex',
            position: 'relative',
          },
          theme.typography.bodyMediumBold,
          variantSwitch({
            statement: variant,
            variants: {
              [DATE_PICKER_CELL_VARIANTS.DEFAULT]: {
                borderRadius: theme.borderRadius.medium,
                color: today
                  ? theme.tokens.colors.text.selected.value
                  : theme.tokens.colors.text.primary.value,

                '&:hover': {
                  backgroundColor:
                    theme.tokens.colors.background.neutral.hover.value,
                },

                '&:focus': {
                  backgroundColor: theme.tokens.colors.background.primary.value,
                  boxShadow: `inset 0px 0px 0px 1px ${theme.tokens.colors.border.focused.value}, inset ${theme.halos.focus}`,
                },

                '&:active': {
                  backgroundColor:
                    theme.tokens.colors.background.neutral.pressed.value,
                  boxShadow: today
                    ? `inset 0px 0px 0px 1px ${theme.tokens.colors.border.selected.value}`
                    : 'none',
                },

                ...(disabled && {
                  color: theme.tokens.colors.text.inactive.value,
                  backgroundColor: theme.tokens.colors.background.primary.value,
                  cursor: 'not-allowed',

                  '&:hover': {
                    boxShadow: 'none',
                    backgroundColor:
                      theme.tokens.colors.background.primary.value,
                  },

                  '&:focus, &:active': {
                    backgroundColor:
                      theme.tokens.colors.background.primary.value,
                    boxShadow: 'none',
                  },
                }),

                ...(selected && {
                  color: theme.tokens.colors.text.inverse.value,
                  backgroundColor:
                    theme.tokens.colors.background.selected.value,
                  '&:hover, &:focus, &:active': {
                    boxShadow: 'none',
                    backgroundColor:
                      theme.tokens.colors.background.selected.value,
                  },
                }),
              },
              [DATE_PICKER_CELL_VARIANTS.RANGE_MIDDLE]: {
                borderRadius: theme.borderRadius.none,
                backgroundColor:
                  theme.tokens.colors.background.selected.subtler.value,
                '&:hover': {
                  color: theme.tokens.colors.text.inverse.value,
                  backgroundColor: theme.tokens.colors.background.hover.value,
                },
              },
              [DATE_PICKER_CELL_VARIANTS.RANGE_START]: {
                color: theme.tokens.colors.text.inverse.value,
                borderRadius: `${theme.borderRadius.medium}px ${theme.borderRadius.none} ${theme.borderRadius.none} ${theme.borderRadius.medium}px`,
                backgroundColor: theme.tokens.colors.background.selected.value,
                '&:hover': {
                  color: theme.tokens.colors.text.inverse.value,
                  backgroundColor: theme.tokens.colors.background.hover.value,
                },
              },
              [DATE_PICKER_CELL_VARIANTS.RANGE_END]: {
                color: theme.tokens.colors.text.inverse.value,
                borderRadius: `${theme.borderRadius.none} ${theme.borderRadius.medium}px  ${theme.borderRadius.medium}px ${theme.borderRadius.none}`,
                backgroundColor: theme.tokens.colors.background.selected.value,
                '&:hover': {
                  color: theme.tokens.colors.text.inverse.value,
                  backgroundColor: theme.tokens.colors.background.hover.value,
                },
              },
              [DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_MIDDLE]: {
                borderRadius: theme.borderRadius.none,
                backgroundColor:
                  theme.tokens.colors.background.selected.subtler.value,
                '&:hover': {
                  color: theme.tokens.colors.text.inverse.value,
                  backgroundColor: theme.tokens.colors.background.hover.value,
                },
              },
              [DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_START]: {
                color: theme.tokens.colors.text.inverse.value,
                borderRadius: `${theme.borderRadius.medium}px ${theme.borderRadius.none} ${theme.borderRadius.none} ${theme.borderRadius.medium}px`,
                backgroundColor: theme.tokens.colors.background.selected.value,
                '&:hover': {
                  color: theme.tokens.colors.text.inverse.value,
                  backgroundColor: theme.tokens.colors.background.hover.value,
                },
              },
              [DATE_PICKER_CELL_VARIANTS.OUTSIDE_RANGE_END]: {
                color: theme.tokens.colors.text.inverse.value,
                borderRadius: `${theme.borderRadius.none} ${theme.borderRadius.medium}px  ${theme.borderRadius.medium}px ${theme.borderRadius.none}`,
                backgroundColor: theme.tokens.colors.background.selected.value,
                '&:hover': {
                  color: theme.tokens.colors.text.inverse.value,
                  backgroundColor: theme.tokens.colors.background.hover.value,
                },
              },
              [DATE_PICKER_CELL_VARIANTS.OUTSIDE]: {
                borderRadius: theme.borderRadius.medium,
                color: theme.tokens.colors.text.inactive.value,
                backgroundColor: theme.tokens.colors.background.primary.value,

                '&:hover, &:focus, &:active': {
                  backgroundColor:
                    theme.tokens.colors.background.secondary.value,
                },
              },
              [DATE_PICKER_CELL_VARIANTS.SPACER]: {
                cursor: 'default',
                pointerEvents: 'none',
                ...theme.typography.bodyMedium,
              },
            },
          }),
          style,
        ]}
      >
        {props.children}
        {hasEvent && (
          <div
            style={{
              position: 'absolute',
              top: 30,
              borderRadius: theme.borderRadius.circle,
              backgroundColor: selected
                ? theme.tokens.colors.text.inverse.value
                : theme.tokens.colors.text.primary.value,
              minHeight: 6,
              minWidth: 6,
              height: 6,
              width: 6,
              maxHeight: 6,
              maxWidth: 6,
            }}
          />
        )}
      </BasicButton>
    );

    if (tooltipContent) {
      return (
        <Tooltip
          variant={Tooltip.VARIANTS.DARK}
          placement={Tooltip.PLACEMENTS.TOP}
          content={tooltipContent}
          delayShow={0}
          delayHide={0}
          getTargetProps={() => ({
            style: {
              width: 40,
              height: 40,
            },
          })}
        >
          {children}
        </Tooltip>
      );
    }

    return children;
  },
);

Cell.displayName = 'Cell';

Cell.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Day value */
  children: PropTypes.any,
  /**  The style variant */
  variant: PropTypes.oneOf(Object.values(DATE_PICKER_CELL_VARIANTS)),
  /** Whether this day is selected, does not apply when variant is a range variant */
  selected: PropTypes.bool,
  /** Whether this day is the today day, does not apply when variant is a range variant */
  today: PropTypes.bool,
  /** Whether this day is the restricted */
  disabled: PropTypes.bool,
  /** Tooltip content */
  tooltipContent: PropTypes.any,
};

export default Object.assign(Cell, { VARIANTS: DATE_PICKER_CELL_VARIANTS });
